import { CSSProperties, useMemo } from "react";
import { ToastProviderContextProps, useToasts } from "./ToastProvider";
import { ToastProps } from "./Toast";

/**
 * Animation duration for toast transitions in milliseconds
 */
export const ANIMATION_DURATION = 300; // ms

/**
 * Hook for calculating position styles for the show more button
 * @returns CSS properties for positioning the show more button
 */
export const useShowMoreButtonPositionStyles = (): CSSProperties => {
  const { toastHeights, toastsBeforeStack } = useToasts();
  const totalToasts = toastHeights.length;
  const extraSpace = totalToasts > toastsBeforeStack + 1 ? "2rem" : "1rem";
  const top = useMemo(() => {
    if (totalToasts > toastsBeforeStack) {
      return toastHeights
        .filter((_t, i) => i >= totalToasts - toastsBeforeStack)
        .map((t) => t.height)
        .reduce((t, c) => t + c);
    }
    return "unset";
    // toastHeights will force update at same time as totalToasts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastHeights, toastsBeforeStack]);

  return {
    // -0.75px fixes hover issue when 1rem != 16px due to pixel rounding
    top: `calc(${top}px + ${extraSpace} - 0.75px)`,
    position: "absolute",
    display: totalToasts > toastsBeforeStack ? undefined : "none",
  };
};

const STACKED_TOAST_HEIGHT = "5.3125rem"; // border + padding + title + gap + message
const STACKED_TOAST_PEEK_HEIGHT = "1rem";
const TOAST_GAP = `${24 / 16}rem`;

/**
 * Hook for calculating position styles for individual toasts
 * @param index - The index of the toast in the stack
 * @returns CSS properties for positioning the toast
 */
export const useToastPositionStyles = (
  index: number,
): CSSProperties | undefined => {
  const {
    toasts,
    toastHeights,
    getToastStackHeight,
    toastsBeforeStack,
    extraToastsVisible,
  } = useToasts();
  const toastCount = toasts.length;
  const toasterHeight = getToastStackHeight(index);

  return useMemo(() => {
    if (index === toastCount - 1) {
      return {
        top: TOAST_GAP,
        zIndex: 3,
      };
    } else if (
      // all toasts when show more is clicked
      extraToastsVisible ||
      // or visible toasts when stacked
      (!extraToastsVisible &&
        (toastCount <= toastsBeforeStack ||
          (toastCount > toastsBeforeStack &&
            index >= toastCount - toastsBeforeStack)))
    ) {
      return {
        top: `calc(${toasterHeight}px + ${TOAST_GAP})`,
      };

      // stacked toasts
    } else {
      if (toastCount > toastsBeforeStack) {
        // first stack
        if (index === toastCount - toastsBeforeStack - 1) {
          return {
            top: `calc(${toasterHeight || 0}px - ${STACKED_TOAST_HEIGHT} +
              ${STACKED_TOAST_PEEK_HEIGHT})`,
            zIndex: 1,
          };
          // second stack and hidden toasts
        } else if (index <= toastCount - toastsBeforeStack - 2) {
          return {
            top: `calc(${toasterHeight || 0}px -
              ${STACKED_TOAST_HEIGHT} 
             + ${STACKED_TOAST_PEEK_HEIGHT} * 2)`,
            zIndex: 0,
          };
        }
      }
    }
    return undefined;
    // only run when toastHeights changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toastHeights]);
};

/**
 * Adds or updates the height of a toast in the height tracking system
 * @param toastId - The unique identifier of the toast
 * @param toastHeight - The height of the toast in pixels
 * @param setToastHeights - Function to update toast heights
 */
export const addOrUpdateToastHeight = (
  toastId: string,
  toastHeight: number,
  setToastHeights: ToastProviderContextProps["setToastHeights"],
) => {
  setToastHeights((prev) => {
    // update height if toast height is defined
    if (prev.find((t) => t.id === toastId)) {
      return prev.map((t) => {
        if (t.id === toastId) {
          return { ...t, height: toastHeight };
        }
        return t;
      });
    }
    // otherwise set height for the first time
    return [
      ...prev,
      {
        id: toastId,
        height: toastHeight,
      },
    ];
  });
};

const setToastsAfterClose = (
  setToasts: ToastProviderContextProps["setToasts"],
  setToastHeights: ToastProviderContextProps["setToastHeights"],
  id?: string,
) => {
  setToasts((prev) => (id ? prev.filter((t) => t.id !== id) : []));
  setToastHeights((prev) => (id ? prev.filter((t) => t.id !== id) : []));
};

/**
 * Closes a toast with animation support
 * @param setToasts - Function to update toasts
 * @param setToastHeights - Function to update toast heights
 * @param closingToastClassName - CSS class for closing animation
 * @param id - Optional toast ID to close specific toast, or undefined to close all
 */
export const closeToast = (
  setToasts: ToastProviderContextProps["setToasts"],
  setToastHeights: ToastProviderContextProps["setToastHeights"],
  closingToastClassName: ToastProps["toastClassName"],
  id?: string,
) => {
  const prefersReducedMotion = globalThis.matchMedia(
    `(prefers-reduced-motion: reduce)`,
  ).matches;

  if (prefersReducedMotion) {
    setToastsAfterClose(setToasts, setToastHeights, id);
  } else {
    // show the close animation
    setToasts((prev) =>
      prev.map((t) => {
        if (!id || t.id === id) {
          return {
            ...t,
            toastClassName: closingToastClassName,
          };
        }
        return t;
      }),
    );
    // actually remove the toast
    setTimeout(() => {
      setToastsAfterClose(setToasts, setToastHeights, id);
    }, ANIMATION_DURATION - 10);
  }
};

/**
 * Hook that returns a function to close toasts
 * @param closingToastClassName - CSS class for closing animation
 * @returns Function to close toasts by ID or all toasts
 */
export const useCloseToast = (
  closingToastClassName: ToastProps["toastClassName"],
) => {
  const { setToasts, setToastHeights } = useToasts();
  return (id?: string) => {
    closeToast(setToasts, setToastHeights, closingToastClassName, id);
  };
};
